import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import { TOPIC_TAGS } from '../../lib/constants';
import { Lockup } from '../../ui/Lockup';
import { LangToggle, useI18n } from '../../i18n';
import { useDocumentTitle } from '../../ui/a11y';
import { Bars, Suppressed } from '../../ui/DataBits';
import { MoodSplit } from '../../ui/MoodSplit';
import { WindowCell, type Window } from '../head/HeadInsights';

type Council = {
  status: 'ok' | 'not_found';
  school?: string;
  expires_at?: string;
  floor?: number;
  weeks?: { from: string; to: string; school: Window; topics: Record<string, number | null> | null }[];
};

// J7: what the student council sees through the head's link — school-level
// complete weeks only, floor 10. No sign-in; the link is the key.
export function CouncilView() {
  const { token = '' } = useParams<{ token: string }>();
  const { t, locale } = useI18n();
  const [data, setData] = useState<Council | null>(null);
  useDocumentTitle(t['cv.title']);
  useEffect(() => {
    supabase.rpc('council_view', { p_token: token }).then(({ data: d }) => setData((d as Council) ?? { status: 'not_found' }));
  }, [token]);
  const dfmt = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'numeric' });
  const last = data?.weeks?.[data.weeks.length - 1];

  return (
    <div className="statement-page">
      <header className="statement-top">
        <Lockup />
        <LangToggle />
      </header>
      <main className="statement" id="main">
        <h1>{t['cv.title']}</h1>
        {!data && <p role="status">{t.loading}</p>}
        {data?.status === 'not_found' && <p>{t['cv.notFound']}</p>}
        {data?.status === 'ok' && data.weeks && (
          <>
            <p><strong>{data.school}</strong></p>
            <p className="form-hint">{t['cv.intro'](data.floor ?? 10)}</p>
            <div className="table-wrap" tabIndex={0} role="region" aria-label={t['cv.weeks']}>
              <table className="table">
                <thead><tr><th scope="col">{t['h2.week']}</th><th scope="col">{t['h2.school']}</th></tr></thead>
                <tbody>
                  {[...data.weeks].reverse().map((w) => (
                    <tr key={w.from}><th scope="row">{`${dfmt.format(new Date(w.from))}–${dfmt.format(new Date(w.to))}`}</th><td><WindowCell w={w.school} /></td></tr>
                  ))}
                </tbody>
              </table>
            </div>
            {last && last.school.status === 'ok' ? (
              <>
                <h2 id="cv-mood">{t['h2.lastWeekMood']}</h2>
                <MoodSplit labelledBy="cv-mood" result={last.school} />
                {last.topics && (
                  <>
                    <h2 id="cv-topics">{t['h2.lastWeekTopics']}</h2>
                    <Bars labelledBy="cv-topics" total={last.school.total}
                      data={TOPIC_TAGS.map((tg) => ({ key: tg.value, emoji: tg.emoji, label: t[`topic.${tg.value}` as 'topic.exams'], count: last.topics?.[tg.value] ?? null }))} />
                  </>
                )}
              </>
            ) : (
              <Suppressed title={t['sup.insufficientTitle']}>{t['h2.windowHidden']}</Suppressed>
            )}
            <p className="form-hint">{t['cv.expires'](new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(data.expires_at!)))}</p>
          </>
        )}
        <p>
          <Link to="/trust">{t['cp.trustTitle']}</Link> · <Link to="/accessibility">{t['a11y.link']}</Link>
        </p>
      </main>
    </div>
  );
}
